<template>
  <HSelect
    :id="props.id"
    v-model="modelValue"
    :name="props.name"
    class="form-select"
    :items="itemsWithDefault"
    :required="isRequired"
    :multiple="props.multiple"
    @change="(v) => emit('change', v)"
  />
</template>

<script setup>
import { computed, watch, onMounted, onUnmounted, inject } from 'vue'
import { HSelect } from '@cnd_fe/ui'

const modelValue = defineModel({ type: null })

const props = defineProps({
  id: { type: String, required: true },
  name: { type: String, required: true },
  items: { type: Array, default: () => [] },
  useDefaultSelect: { type: Boolean, default: false },
  defaultSelectLabel: { type: String, default: '선택' },
  required: { type: Boolean, default: false },
  multiple: { type: Boolean, default: false }
})

const emit = defineEmits(['change'])

const registerExtraField = inject('registerExtraField', null)
const unregisterExtraField = inject('unregisterExtraField', null)

if (registerExtraField && props.name) {
  onMounted(() => registerExtraField(props.name, () => modelValue.value))
  onUnmounted(() => unregisterExtraField?.(props.name))
}

const isRequired = computed(() => props.required || props.useDefaultSelect)

const normalizedItems = computed(() =>
  (props.items ?? []).map((it, i) => ({
    key: it.key ?? it.value ?? `opt-${i}`,
    ...it
  }))
)

const itemsWithDefault = computed(() => {
  if (!props.useDefaultSelect) return normalizedItems.value

  // 이미 value:'' 옵션이 있으면 그대로 사용
  if (normalizedItems.value[0]?.value === '') return normalizedItems.value

  return [
    { key: '__default__', value: '', label: props.defaultSelectLabel, disabled: true },
    ...normalizedItems.value
  ]
})

const isEmpty = (v) => v === undefined || v === null || v === ''

const hasValueInItems = (v) =>
  itemsWithDefault.value.some((it) => String(it.value) === String(v))

const ensureValue = () => {
  if (!itemsWithDefault.value.length) return

  // multiple일 때는 배열 유지, 빈 배열도 유효
  if (props.multiple) {
    if (modelValue.value === undefined || modelValue.value === null) {
      modelValue.value = []
    } else if (!Array.isArray(modelValue.value)) {
      // 문자열이나 단일 값이면 배열로 변환
      modelValue.value = modelValue.value ? [modelValue.value] : []
    }
    return
  }

  // 값이 이미 들어있고, items에 존재하면 건드리지 않음
  if (!isEmpty(modelValue.value) && hasValueInItems(modelValue.value)) return

  // useDefaultSelect=true : 빈 값이면 '' 유지(placeholder)
  if (props.useDefaultSelect) {
    if (modelValue.value === undefined || modelValue.value === null) {
      modelValue.value = ''
    }
    return
  }

  // useDefaultSelect=false : 빈 값이면 첫 옵션으로
  if (isEmpty(modelValue.value)) {
    modelValue.value = itemsWithDefault.value[0].value
    return
  }

  // 값은 있는데 items에 없으면(로드 타이밍/불일치) 첫 옵션으로 보정
  modelValue.value = itemsWithDefault.value[0].value
}

watch(
  () => [props.useDefaultSelect, props.items?.length, modelValue.value],
  ensureValue,
  { immediate: true }
)
// splice로 내부 값만 바뀌는 케이스까지 커버
watch(() => props.items, ensureValue, { deep: true })
</script>

<style scoped>
select.error {
  border-color: #dc3545 !important;
  background: #fff0f0;
}

.form-select {
  width: auto;
  max-width: inherit;
  min-width: 125px;
}

:deep(select.short-width){
  max-width: 80px !important;
}
</style>
